// src/modules/cmt-pelotao/Avaliacao360.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { useAuth } from '../../context/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';

// Ícones
const EyeIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;

const queimogramaQuestions = [
    { label: 'Conhecimento Técnico', name: 'conhecimento_tecnico' },
    { label: 'Pontualidade', name: 'pontualidade' },
    { label: 'Apresentação Pessoal', name: 'apresentacao_pessoal' },
    { label: 'Interação com a Tropa', name: 'interacao_tropa' },
    { label: 'Dedicação ao Ensino', name: 'dedicacao_ensino' },
    { label: 'Estabilidade Emocional', name: 'estabilidade_emocional' },
    { label: 'Capacidade de Decisão', name: 'capacidade_decisao' },
    { label: 'Ética e Compromisso', name: 'etica_compromisso' },
    { label: 'Liderança', name: 'lideranca' },
    { label: 'Nota Geral', name: 'nota_geral' },
];

export const Avaliacao360 = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);

  const [escolhas, setEscolhas] = useState([]);
  const [relatorios, setRelatorios] = useState([]);
  const [queimogramas, setQueimogramas] = useState([]);
  const [allLocais, setAllLocais] = useState([]); 
  const [pelotaoNome, setPelotaoNome] = useState('');

  const [qFilterNome, setQFilterNome] = useState('');
  const [qFilterLocal, setQFilterLocal] = useState('');
  const [selectedOfficerDetails, setSelectedOfficerDetails] = useState(null);

  useEffect(() => {
    // CORREÇÃO: Removemos o return imediato se não tiver pelotao_id,
    // para permitir que o SOE veja os dados.
    const pelotaoId = userProfile?.pelotao_id;

    if (pelotaoId) {
        getDoc(doc(db, paths.pelotoes, pelotaoId)).then(snap => {
            if (snap.exists()) setPelotaoNome(snap.data().nome);
        });
    } else {
        setPelotaoNome("Visão Geral (Todos)");
    }

    // Buscando dados necessários
    // Se tiver pelotaoId, filtra. Se for SOE (sem pelotaoId), busca tudo.
    let qEscolhas;
    if (pelotaoId) {
        qEscolhas = query(collection(db, paths.escolhas), where("pelotao_id", "==", pelotaoId));
    } else {
        qEscolhas = query(collection(db, paths.escolhas));
    }
    
    const unsubEscolhas = onSnapshot(qEscolhas, (snap) => setEscolhas(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    const qRelatorios = query(collection(db, paths.relatoriosEstagio)); 
    const unsubRelatorios = onSnapshot(qRelatorios, (snap) => setRelatorios(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    const qQueimogramas = collection(db, paths.queimogramas || "queimogramas");
    const unsubQueimogramas = onSnapshot(qQueimogramas, (snap) => setQueimogramas(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    const unsubLocais = onSnapshot(collection(db, paths.locais), (snap) => setAllLocais(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    setLoading(false);

    return () => { 
        unsubEscolhas(); 
        unsubRelatorios(); 
        unsubLocais(); 
        unsubQueimogramas();
    };
  }, [userProfile]);

  const relatoriosMap = useMemo(() => {
      const map = {};
      relatorios.forEach(r => { map[r.escolha_id] = r; });
      return map;
  }, [relatorios]);

  const dadosQueimogramaAgrupados = useMemo(() => {
      const agrupado = {};

      queimogramas.forEach(q => {
          const relatorio = relatoriosMap[q.escolha_id];
          const escolha = escolhas.find(e => e.id === q.escolha_id);
          
          let nomeOficial = relatorio?.cadete?.qra_preceptor || relatorio?.preceptor?.posto_nome || "Oficial Não Identificado";
          nomeOficial = nomeOficial.trim().toUpperCase();

          let nomeLocal = "Local N/D";
          if (q.local_id) {
              const localObj = allLocais.find(l => l.id === q.local_id);
              if (localObj) nomeLocal = localObj.nome;
          } else if (escolha) {
              nomeLocal = escolha.local_nome;
          }

          const key = `${nomeOficial}-${nomeLocal}`;

          if (!agrupado[key]) {
              agrupado[key] = {
                  oficial: nomeOficial,
                  local: nomeLocal,
                  totalAvaliacoes: 0,
                  somaNotas: {},
                  comentarios: []
              };
              queimogramaQuestions.forEach(qst => agrupado[key].somaNotas[qst.name] = 0);
          }

          const entry = agrupado[key];
          entry.totalAvaliacoes += 1;

          if (q.avaliacao) {
              queimogramaQuestions.forEach(qst => {
                  const valor = q.avaliacao[qst.name];
                  if (typeof valor === 'number') {
                      entry.somaNotas[qst.name] += valor;
                  }
              });

              if (q.avaliacao.informacoes_complementares && q.avaliacao.informacoes_complementares.trim()) {
                  entry.comentarios.push(q.avaliacao.informacoes_complementares);
              }
          }
      });

      return Object.values(agrupado).map(item => {
          const medias = {};
          queimogramaQuestions.forEach(qst => {
              const mediaQuesito = item.somaNotas[qst.name] / item.totalAvaliacoes;
              medias[qst.name] = mediaQuesito;
          });

          return {
              ...item,
              medias,
              mediaFinal: medias['nota_geral'] || 0
          };
      }).sort((a, b) => b.mediaFinal - a.mediaFinal);

  }, [queimogramas, relatoriosMap, escolhas, allLocais]);

  const queimogramaFiltrado = useMemo(() => {
      return dadosQueimogramaAgrupados.filter(item => {
          const matchNome = qFilterNome ? item.oficial.toLowerCase().includes(qFilterNome.toLowerCase()) : true;
          const matchLocal = qFilterLocal ? item.local === qFilterLocal : true;
          return matchNome && matchLocal;
      });
  }, [dadosQueimogramaAgrupados, qFilterNome, qFilterLocal]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50 pb-10 -m-4">
      <header className="bg-white shadow-sm sticky top-0 z-10 mb-6">
          <div className="w-full px-6 py-4 flex items-center space-x-4">
            <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão" className="h-12 w-12" />
            <div>
                <h1 className="text-4xl font-bold text-gray-800">Avaliação 360º</h1>
                <p className="text-sm text-gray-600">Tenentes: <span className="font-semibold text-blue-600">{pelotaoNome || 'Geral'}</span></p>
            </div>
          </div>
      </header>

      <main className="w-full px-6 animate-fade-in">
        <div className="space-y-4">
            {/* Filtros */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 w-full">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Buscar Oficial (Nome/Posto)</label>
                    <input 
                        type="text" 
                        value={qFilterNome} 
                        onChange={(e) => setQFilterNome(e.target.value)} 
                        placeholder="Ex: Cap Silva" 
                        className="w-full border p-2 rounded text-sm" 
                    />
                </div>
                <div className="flex-1 w-full">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filtrar por Unidade</label>
                    <select 
                        value={qFilterLocal} 
                        onChange={(e) => setQFilterLocal(e.target.value)} 
                        className="w-full border p-2 rounded text-sm bg-white"
                    >
                        <option value="">Todas as Unidades</option>
                        {[...new Set(dadosQueimogramaAgrupados.map(i => i.local))].sort().map(local => (
                            <option key={local} value={local}>{local}</option>
                        ))}
                    </select>
                </div>
                <Button onClick={() => {setQFilterNome(''); setQFilterLocal('')}} variant="secondary" className="h-10">Limpar</Button>
            </div>

            {/* Tabela de Resultados */}
            <div className="bg-white shadow rounded-lg overflow-x-auto border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oficial</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidade</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Avaliações</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Média Geral</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {queimogramaFiltrado.length === 0 ? (
                            <tr><td colSpan="5" className="px-6 py-4 text-center text-gray-500">Nenhuma avaliação encontrada com os filtros atuais.</td></tr>
                        ) : (
                            queimogramaFiltrado.map((item, idx) => {
                                const nota = item.mediaFinal.toFixed(1);
                                let badgeColor = 'bg-gray-100 text-gray-800';
                                if (item.mediaFinal >= 9) badgeColor = 'bg-green-100 text-green-800';
                                else if (item.mediaFinal >= 7) badgeColor = 'bg-blue-100 text-blue-800';
                                else badgeColor = 'bg-red-100 text-red-800';

                                return (
                                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{item.oficial}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.local}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-700">{item.totalAvaliacoes}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className={`px-3 py-1 inline-flex text-sm leading-5 font-bold rounded-full ${badgeColor}`}>
                                                {nota}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button 
                                                onClick={() => setSelectedOfficerDetails(item)}
                                                className="text-blue-600 hover:text-blue-900 flex items-center justify-end gap-1 ml-auto transition-colors"
                                            >
                                                <EyeIcon /> Detalhes
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
      </main>

      {/* --- MODAL DE DETALHES --- */}
      <Modal show={!!selectedOfficerDetails} onClose={() => setSelectedOfficerDetails(null)} title={`Detalhes da Avaliação: ${selectedOfficerDetails?.oficial}`} size="2xl">
          {selectedOfficerDetails && (
              <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                          <div><span className="font-bold text-blue-800">Unidade:</span> {selectedOfficerDetails.local}</div>
                          <div><span className="font-bold text-blue-800">Total Avaliações:</span> {selectedOfficerDetails.totalAvaliacoes}</div>
                      </div>
                  </div>
                  <div>
                      <h4 className="font-bold text-gray-800 mb-3 border-b pb-1">Média por Quesito (0-10)</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                          {queimogramaQuestions.filter(q => q.name !== 'nota_geral').map(q => (
                              <div key={q.name} className="flex justify-between items-center text-sm border-b border-gray-100 pb-1">
                                  <span className="text-gray-600">{q.label}</span>
                                  <span className="font-bold text-blue-600">{selectedOfficerDetails.medias[q.name].toFixed(2)}</span>
                              </div>
                          ))}
                      </div>
                  </div>
                  <div>
                      <h4 className="font-bold text-gray-800 mb-3 border-b pb-1">Comentários (Informações Complementares)</h4>
                      {selectedOfficerDetails.comentarios.length === 0 ? (
                          <p className="text-gray-400 italic text-sm">Nenhum comentário registrado.</p>
                      ) : (
                          <ul className="space-y-3 max-h-60 overflow-y-auto pr-2">
                              {selectedOfficerDetails.comentarios.map((comentario, index) => (
                                  <li key={index} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm text-gray-700 italic relative">
                                      <span className="absolute top-2 left-2 text-gray-300 font-serif text-3xl opacity-50">"</span>
                                      <p className="pl-4">{comentario}</p>
                                  </li>
                              ))}
                          </ul>
                      )}
                  </div>
                  <div className="flex justify-end pt-4 border-t">
                      <Button onClick={() => setSelectedOfficerDetails(null)} variant="primary">Fechar</Button>
                  </div>
              </div>
          )}
      </Modal>
    </div>
  );
};